import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Loader2, Send, Clock } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import {
  QuestionRenderer,
  emptyResponseFor,
  type QuestionResponseValue,
} from '@/components/Assessment/QuestionRenderer';
import { useAuth } from '@/lib/auth-store';
import { api, type AttemptShape } from '@/lib/api';
import { useAssessmentByItemQuery, useAssessmentTreeQuery } from '@/hooks/queries/useAssessment';
import { ApiError } from '@/lib/api';

/**
 * Student-facing assessment taker. Routes:
 *   /c/:orgSlug/:courseSlug/assessments/:itemId/take
 *
 * Lifecycle:
 *   1. Resolve the assessment from the module_item id
 *   2. Start (or resume) an attempt
 *   3. Hydrate any prior responses from autosave
 *   4. Per-question render with autosave-on-change
 *   5. Submit → grading engine on the server → AttemptResult page
 */
export function AssessmentTaker() {
  const { state } = useAuth();
  const { orgSlug = '', courseSlug = '', itemId = '' } = useParams();
  const navigate = useNavigate();

  const byItem = useAssessmentByItemQuery(
    state.status === 'authenticated' ? state.token : '',
    itemId,
  );
  const tree = useAssessmentTreeQuery(
    state.status === 'authenticated' ? state.token : '',
    byItem.data?.assessment.id ?? null,
  );

  const [attempt, setAttempt] = useState<AttemptShape | null>(null);
  const [responses, setResponses] = useState<Record<string, QuestionResponseValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);

  // Start the attempt as soon as we have an assessment id, then hydrate any
  // persisted draft responses so a returning student's tab close doesn't
  // lose their work. startAttempt is idempotent on the server: a returning
  // student gets back their existing in-progress attempt id, which still
  // has its prior question_responses rows attached. The seed-from-tree
  // effect below uses `!(q.id in next)` so it never overwrites a restored
  // draft — natural ordering safety.
  useEffect(() => {
    if (state.status !== 'authenticated') return;
    if (!byItem.data?.assessment.id) return;
    if (attempt) return;
    void api.assessments
      .startAttempt(state.token, byItem.data.assessment.id)
      .then(async ({ attempt: a }) => {
        setAttempt(a);
        try {
          const { responses: drafts } = await api.assessments.listResponses(state.token, a.id);
          setResponses((prev) => {
            const next = { ...prev };
            for (const d of drafts) {
              try {
                next[d.questionId] = JSON.parse(d.responseJson) as QuestionResponseValue;
              } catch {
                // Draft body got corrupted server-side somehow — leave the
                // empty default in place rather than crashing the page.
              }
            }
            return next;
          });
          if (drafts.length > 0) setRestored(true);
        } catch {
          // Restore is best-effort. If it fails the student starts fresh —
          // unfortunate but recoverable; their next autosave will overwrite.
        }
      })
      .catch((e) => {
        if (e instanceof ApiError && e.code === 'attempt_limit_reached') {
          setError("You've reached the attempt limit for this assessment.");
        } else {
          setError("Couldn't start your attempt. Try again.");
        }
      });
  }, [state, byItem.data?.assessment.id, attempt]);

  // Initialise responses from the question list once the tree loads.
  useEffect(() => {
    if (!tree.data) return;
    setResponses((prev) => {
      const next = { ...prev };
      for (const q of tree.data.questions) {
        if (!(q.id in next)) {
          next[q.id] = emptyResponseFor(q.type, q.options);
        }
      }
      return next;
    });
  }, [tree.data]);

  if (state.status !== 'authenticated') return null;
  if (byItem.isLoading || tree.isLoading) {
    return (
      <Shell>
        <div className="h-32 animate-pulse rounded-2xl bg-card" />
      </Shell>
    );
  }
  if (!byItem.data || !tree.data) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">Couldn&apos;t load this assessment.</p>
      </Shell>
    );
  }

  const { assessment, questions } = tree.data;
  const token = state.token;

  const onSubmit = async () => {
    if (!attempt) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.assessments.submitAttempt(token, attempt.id);
      navigate(`/c/${orgSlug}/${courseSlug}/assessments/${itemId}/result/${attempt.id}`, {
        state: { result },
      });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'already_submitted') {
        navigate(`/c/${orgSlug}/${courseSlug}/assessments/${itemId}/result/${attempt.id}`);
        return;
      }
      setError("EduBorg couldn't submit just now. Your answers are saved — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell>
      <Link
        to={`/c/${orgSlug}/${courseSlug}`}
        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden /> Back to course
      </Link>

      <header className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{assessment.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {questions.length} question{questions.length === 1 ? '' : 's'} ·{' '}
            {assessment.totalPoints} pt{assessment.totalPoints === 1 ? '' : 's'} total
          </p>
        </div>
        {attempt?.expiresAt && (
          <Timer expiresAt={attempt.expiresAt} onExpire={() => void onSubmit()} />
        )}
      </header>

      {assessment.description && (
        <p className="mt-3 text-sm text-muted-foreground">{assessment.description}</p>
      )}

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      <ol className="mt-6 space-y-6">
        {questions.map((q, ix) => (
          <motion.li
            key={q.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: ix * 0.02 }}
            className="rounded-2xl border border-border bg-card p-5 shadow-sm"
          >
            <header className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <span>Q{ix + 1}</span>
              <span className="text-foreground/40">•</span>
              <span>
                {q.points} {q.points === 1 ? 'pt' : 'pts'}
              </span>
            </header>
            {q.promptHtml && (
              <p className="mb-4 whitespace-pre-wrap text-sm leading-relaxed">{q.promptHtml}</p>
            )}
            <Autosaver
              token={token}
              attemptId={attempt?.id ?? null}
              questionId={q.id}
              value={responses[q.id] ?? null}
            />
            <QuestionRenderer
              question={q}
              value={responses[q.id] ?? null}
              onChange={(v) => setResponses((prev) => ({ ...prev, [q.id]: v }))}
            />
          </motion.li>
        ))}
      </ol>

      <footer className="mt-8 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {restored
            ? 'Your saved answers were restored. Autosaves as you type.'
            : 'Your answers autosave as you type. Submit when you’re ready.'}
        </p>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || !attempt}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:translate-y-[-1px] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Send className="h-4 w-4" aria-hidden />
          )}
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </footer>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AppHeader />
      <main className="container max-w-3xl py-8">{children}</main>
    </div>
  );
}

/* ─── Timer ────────────────────────────────────────────────────────────── */

function Timer({ expiresAt, onExpire }: { expiresAt: number; onExpire: () => void }) {
  const [ms, setMs] = useState(Math.max(0, expiresAt - Date.now()));
  const fired = useRef(false);

  useEffect(() => {
    const id = window.setInterval(() => {
      const remaining = Math.max(0, expiresAt - Date.now());
      setMs(remaining);
      if (remaining === 0 && !fired.current) {
        fired.current = true;
        onExpire();
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, onExpire]);

  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-medium tabular-nums">
      <Clock className="h-4 w-4 text-accent" aria-hidden />
      <span>
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </span>
    </div>
  );
}

/* ─── Autosaver — debounced background save per question response ──── */

function Autosaver({
  token,
  attemptId,
  questionId,
  value,
}: {
  token: string;
  attemptId: string | null;
  questionId: string;
  value: QuestionResponseValue;
}) {
  const lastSent = useRef<string>('');
  const stableValue = useMemo(() => JSON.stringify(value), [value]);

  useEffect(() => {
    if (!attemptId) return;
    if (stableValue === lastSent.current) return;
    if (value === null) return;

    const timer = window.setTimeout(() => {
      void api.assessments
        .autosaveResponse(token, attemptId, { questionId, responseJson: stableValue })
        .then(() => {
          lastSent.current = stableValue;
        })
        .catch(() => {
          /* silent — next change will retry; submit will catch persistent issues */
        });
    }, 600);

    return () => window.clearTimeout(timer);
  }, [token, attemptId, questionId, stableValue, value]);

  return null;
}
